﻿import { useState, useEffect } from 'react';
import { useAuthStore } from '../hooks/useAuthStore';
import { Calendar, Video, Clock, User, Plus, Copy, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL || '/api';

export default function MyAppointments() {
  const { user, token } = useAuthStore();
  const [appointments, setAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const headers = { Authorization: `Bearer ${token}` };
  const isDoctor = user?.role === 'doctor' || user?.role === 'admin';

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const [upcomingRes, pastRes] = await Promise.all([
        fetch(`${API}/appointments/index.php?filter=upcoming`, { headers }).then(r => r.json()),
        fetch(`${API}/appointments/index.php?filter=past`, { headers }).then(r => r.json())
      ]);
      if (upcomingRes.success) setAppointments(upcomingRes.appointments || []);
      if (pastRes.success) setPastAppointments(pastRes.appointments || []);
    } catch (err) {
      console.error('Failed to load appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const cancelAppointment = async (id) => {
    if (!confirm('Cancel this appointment?')) return;
    try {
      await fetch(`${API}/appointments/index.php`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status: 'cancelled' }),
      });
      loadAppointments();
    } catch {}
  };

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API}/appointments/index.php`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status }),
      });
      loadAppointments();
    } catch {}
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-purple-100 text-purple-800 border-purple-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      no_show: 'bg-gray-100 text-gray-600 border-gray-200'
    };
    return map[status] || 'bg-gray-100 text-gray-600';
  };

  const CopyableVideoLink = ({ roomName }) => {
    const [copied, setCopied] = useState(false);
    const videoUrl = `${window.location.origin}/video-chat?room=${roomName}`;
    const handleCopy = () => {
      navigator.clipboard.writeText(videoUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    };
    return (
      <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
        <p className="text-xs font-semibold text-emerald-700 mb-1">🎥 Video Consultation Link</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-emerald-800 font-mono truncate flex-1 bg-white border border-emerald-200 rounded px-2 py-1">
            {videoUrl}
          </span>
          <button
            onClick={handleCopy}
            title="Copy link"
            className="shrink-0 p-1.5 rounded border border-emerald-300 bg-white hover:bg-emerald-100 text-emerald-700 transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    );
  };

  const AppointmentCard = ({ appt, isPast }) => {
    const scheduledDate = new Date(appt.scheduled_at);
    const isUpcoming = scheduledDate > new Date();
    const otherPerson = isDoctor
      ? `${appt.patient_first} ${appt.patient_last}`
      : appt.doctor_name;
    const otherRole = isDoctor ? 'Patient' : (appt.specialty || 'General Practitioner');
    const otherEmail = isDoctor ? appt.patient_email : appt.doctor_email;

    return (
      <div className={`bg-white rounded-xl border-2 p-5 transition-all ${appt.status === 'cancelled' ? 'border-gray-200 opacity-60' : 'border-gray-200 hover:border-blue-300'}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${isDoctor ? 'bg-purple-50' : 'bg-green-50'}`}>
              {isDoctor ? '👤' : '👨‍⚕️'}
            </div>
            <div>
              <p className="font-bold text-gray-900">{otherPerson}</p>
              <p className="text-sm text-purple-700">{otherRole}</p>
              {otherEmail && <p className="text-xs text-gray-400">{otherEmail}</p>}
              {appt.chief_complaint && (
                <p className="text-sm text-gray-600 mt-2 bg-gray-50 px-3 py-1 rounded-lg">💬 {appt.chief_complaint}</p>
              )}
            </div>
          </div>
          <div className="text-right shrink-0 ml-4">
            <div className="flex items-center space-x-1 mb-1">
              <Calendar className="w-4 h-4 text-gray-400" />
              <p className="text-sm font-medium text-gray-900">
                {scheduledDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
            <div className="flex items-center space-x-1 mb-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <p className="text-sm text-gray-600">
                {scheduledDate.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full border ${getStatusBadge(appt.status)} capitalize`}>
              {appt.status.replace('_', ' ')}
            </span>
          </div>
        </div>

        {/* Video Link Display — always visible for non-cancelled/completed */}
        {appt.video_room_name && appt.status !== 'cancelled' && (
          <CopyableVideoLink roomName={appt.video_room_name} />
        )}

        {/* Actions */}
        {appt.status !== 'cancelled' && appt.status !== 'completed' && (
          <div className="mt-3 flex flex-wrap gap-2 pt-3 border-t border-gray-100">
            {appt.video_room_name && (
              <Link to={`/video-chat?room=${appt.video_room_name}`}
                className="flex items-center space-x-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                <Video className="w-4 h-4" />
                <span>Join Video</span>
              </Link>
            )}
            {isDoctor && appt.status === 'pending' && (
              <button onClick={() => updateStatus(appt.id, 'confirmed')}
                className="bg-purple-700 hover:bg-purple-800 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                ✅ Confirm
              </button>
            )}
            {isDoctor && appt.status === 'confirmed' && (
              <button onClick={() => updateStatus(appt.id, 'completed')}
                className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                ✓ Complete
              </button>
            )}
            {!isPast && (
              <button onClick={() => cancelAppointment(appt.id)}
                className="text-red-600 hover:text-red-700 border border-red-200 hover:border-red-300 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                Cancel
              </button>
            )}
          </div>
        )}
        {appt.status === 'completed' && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <Link to="/letters" className="text-sm text-purple-700 hover:underline">📄 View consultation letters →</Link>
          </div>
        )}
      </div>
    );
  };

  const displayList = tab === 'upcoming' ? appointments : pastAppointments;

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isDoctor ? 'Patient Appointments' : 'My Appointments'}
            </h1>
            <p className="text-gray-500 mt-1">
              {isDoctor ? 'Manage your patient video consultations' : 'Your upcoming and past video consultations'}
            </p>
          </div>
          {!isDoctor && (
            <Link to="/book-appointment"
              className="flex items-center space-x-2 bg-purple-700 hover:bg-purple-800 text-white font-medium px-5 py-2.5 rounded-xl transition-colors">
              <Plus className="w-4 h-4" />
              <span>Book GP</span>
            </Link>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 text-center shadow border border-purple-100">
            <p className="text-2xl font-bold text-purple-700">{appointments.length}</p>
            <p className="text-xs text-gray-500">Upcoming</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow border border-green-100">
            <p className="text-2xl font-bold text-green-600">{pastAppointments.filter(a => a.status === 'completed').length}</p>
            <p className="text-xs text-gray-500">Completed</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow border border-gray-100">
            <p className="text-2xl font-bold text-gray-500">{pastAppointments.filter(a => a.status === 'cancelled').length}</p>
            <p className="text-xs text-gray-500">Cancelled</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex bg-white rounded-xl shadow mb-4 border border-gray-200 overflow-hidden">
          <button onClick={() => setTab('upcoming')} className={`flex-1 py-3 text-sm font-medium transition-colors ${tab === 'upcoming' ? 'bg-purple-700 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
            📅 Upcoming ({appointments.length})
          </button>
          <button onClick={() => setTab('past')} className={`flex-1 py-3 text-sm font-medium transition-colors ${tab === 'past' ? 'bg-purple-700 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
            🕐 Past ({pastAppointments.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-500">Loading appointments...</p>
          </div>
        ) : displayList.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {tab === 'upcoming' ? 'No upcoming appointments' : 'No past appointments'}
            </h3>
            {tab === 'upcoming' && !isDoctor && (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-gray-500">Start with an AI consultation then book a GP appointment</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
                  <Link to="/my-consult-openai" className="bg-purple-700 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-purple-800">
                    💬 Start AI Chat First
                  </Link>
                  <Link to="/book-appointment" className="bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700">
                    📅 Book a GP
                  </Link>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {displayList.map(appt => (
              <AppointmentCard key={appt.id} appt={appt} isPast={tab === 'past'} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
